import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../contexts/LoadingContext';
import { updateProductNearExpiry, removeProductNearExpiry, getActiveNearExpiry, searchProductsForNearExpiry } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';

const NearExpiryPage = () => {
  const { startLoading, stopLoading } = useLoading();
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNearExpiry, setEditingNearExpiry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [nearExpiryForm, setNearExpiryForm] = useState({
    nearExpiryDiscountType: 'percentage',
    nearExpiryDiscountValue: '',
    isNearExpiry: false,
    expiryDate: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch products with near expiry
  useEffect(() => {
    fetchNearExpiryProducts();
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('product-dropdown');
      const searchInput = document.getElementById('product-search-input');
      
      if (isDropdownOpen && dropdown && !dropdown.contains(event.target) && !searchInput?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Search products when query changes
  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      searchProducts();
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery]);

  const fetchNearExpiryProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/products/admin/list', {
        params: {
          status: 'active',
          showDrafts: false,
          limit: 1000
        }
      });
      
      // Filter products that have near expiry enabled
      const productsData = response.data.data.products || response.data.data || [];
      const nearExpiryProducts = productsData.filter(product => 
        product.isNearExpiry && product.expiryDate
      );
      
      setProducts(nearExpiryProducts);
    } catch (error) {
      console.error('Error fetching near expiry products:', error);
      toast.error('Failed to fetch near expiry products');
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async () => {
    try {
      setIsSearching(true);
      const response = await searchProductsForNearExpiry(debouncedSearchQuery);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleOpenModal = (product = null, bulkMode = false) => {
    if (product) {
      setEditingNearExpiry(product);
      setSelectedProduct(product);
      setSelectedProducts([]);
      setIsBulkMode(false);
      setIsDropdownOpen(false);
      setSearchQuery('');
      setNearExpiryForm({
        nearExpiryDiscountType: product.nearExpiryDiscountType || 'percentage',
        nearExpiryDiscountValue: product.nearExpiryDiscountValue || '',
        isNearExpiry: product.isNearExpiry || false,
        expiryDate: product.expiryDate ? new Date(product.expiryDate).toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingNearExpiry(null);
      setSelectedProduct(null);
      setSelectedProducts([]);
      setIsBulkMode(bulkMode);
      setIsDropdownOpen(false);
      setSearchQuery('');
      setNearExpiryForm({
        nearExpiryDiscountType: 'percentage',
        nearExpiryDiscountValue: '',
        isNearExpiry: false,
        expiryDate: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNearExpiry(null);
    setSelectedProduct(null);
    setSelectedProducts([]);
    setIsBulkMode(false);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setFormErrors({});
    setNearExpiryForm({
      nearExpiryDiscountType: 'percentage',
      nearExpiryDiscountValue: '',
      isNearExpiry: false,
      expiryDate: ''
    });
  };

  const handleProductSelect = (product) => {
    if (isBulkMode) {
      setSelectedProducts(prev => {
        const exists = prev.find(p => p._id === product._id);
        if (exists) {
          return prev.filter(p => p._id !== product._id);
        } else {
          return [...prev, product];
        }
      });
      // Keep dropdown open in bulk mode for multiple selections
    } else {
      setSelectedProduct(product);
      setSearchQuery(product.name);
      setIsDropdownOpen(false); // Close dropdown after single selection
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    if (!nearExpiryForm.nearExpiryDiscountValue) {
      toast.error('Please fill in discount value');
      return;
    }

    if (!nearExpiryForm.expiryDate) {
      toast.error('Please set an expiry date');
      return;
    }

    try {
      startLoading();
      let successCount = 0;
      let errorCount = 0;

      for (const product of selectedProducts) {
        try {
          const discountValue = parseFloat(nearExpiryForm.nearExpiryDiscountValue);
          if (discountValue <= 0) {
            errorCount++;
            continue;
          }
          await updateProductNearExpiry(product._id, nearExpiryForm);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error updating near expiry for ${product.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully created near expiry discounts for ${successCount} products`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to create near expiry discounts for ${errorCount} products`);
      }

      handleCloseModal();
      fetchNearExpiryProducts();
    } catch (error) {
      console.error('Error in bulk near expiry creation:', error);
      toast.error('Failed to create bulk near expiry discounts');
    } finally {
      stopLoading();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct && !isBulkMode) {
      toast.error('Please select a product');
      return;
    }

    if (!nearExpiryForm.nearExpiryDiscountValue) {
      toast.error('Please fill in discount value');
      return;
    }

    if (!nearExpiryForm.expiryDate) {
      toast.error('Please set an expiry date');
      return;
    }

    try {
      startLoading();
      
      if (isBulkMode) {
        await handleBulkSubmit(e);
        return;
      }

      await updateProductNearExpiry(selectedProduct._id, nearExpiryForm);
      toast.success('Near expiry discount updated successfully');
      handleCloseModal();
      fetchNearExpiryProducts();
    } catch (error) {
      console.error('Error updating near expiry:', error);
      toast.error('Failed to update near expiry discount');
    } finally {
      stopLoading();
    }
  };

  const handleRemoveNearExpiry = async (productId) => {
    if (!window.confirm('Are you sure you want to remove the near expiry discount for this product?')) {
      return;
    }

    try {
      startLoading();
      await removeProductNearExpiry(productId);
      toast.success('Near expiry discount removed successfully');
      fetchNearExpiryProducts();
    } catch (error) {
      console.error('Error removing near expiry:', error);
      toast.error('Failed to remove near expiry discount');
    } finally {
      stopLoading();
    }
  };

  const formatCurrency = (amount) => `Rs. ${amount?.toLocaleString() || 0}`;

  const getNearExpiryPrice = (regularPrice, discountType, discountValue) => {
    if (!discountValue || !regularPrice) return regularPrice;
    
    if (discountType === 'percentage') {
      return Math.round(regularPrice * (1 - discountValue / 100));
    } else {
      return regularPrice - discountValue;
    }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDiscountDisplay = (discountType, discountValue) => {
    if (discountType === 'percentage') {
      return `${discountValue}% OFF`;
    } else {
      return `Rs. ${discountValue} OFF`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Near Expiry Management</h1>
          <p className="text-admin-slate-600 dark:text-admin-slate-400">Manage products nearing expiry with discounts</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleOpenModal(null, true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Bulk Near Expiry
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="bg-admin-slate-600 hover:bg-admin-slate-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Near Expiry
          </button>
        </div>
      </div>

      {/* Near Expiry Table */}
      <div className="bg-white dark:bg-admin-slate-800 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            <thead className="bg-admin-slate-50 dark:bg-admin-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500 mx-auto"></div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                    No products with near expiry discounts found
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const nearExpiryPrice = getNearExpiryPrice(
                    product.regularPrice, 
                    product.nearExpiryDiscountType, 
                    product.nearExpiryDiscountValue
                  );
                  const daysUntilExpiry = getDaysUntilExpiry(product.expiryDate);
                  
                  return (
                    <tr key={product._id} className="hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 flex-shrink-0">
                            <img
                              className="h-12 w-12 rounded-lg object-cover"
                              src={product.images?.[0] || '/placeholder-product.jpg'}
                              alt={product.name}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                              {product.name}
                            </div>
                            <div className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                              SKU: {product.sku || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-admin-slate-900 dark:text-admin-slate-100">
                          <div className="font-medium">{formatCurrency(nearExpiryPrice)}</div>
                          <div className="text-admin-slate-500 dark:text-admin-slate-400 line-through">
                            {formatCurrency(product.regularPrice)}
                          </div>
                          <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                            {getDiscountDisplay(product.nearExpiryDiscountType, product.nearExpiryDiscountValue)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-admin-slate-900 dark:text-admin-slate-100">
                          {formatExpiryDate(product.expiryDate)}
                        </div>
                        {daysUntilExpiry !== null && (
                          <div className={`text-xs ${
                            daysUntilExpiry <= 7 ? 'text-red-600 dark:text-red-400' :
                            daysUntilExpiry <= 30 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-green-600 dark:text-green-400'
                          }`}>
                            {daysUntilExpiry > 0 ? `${daysUntilExpiry} days left` : 'Expired'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.isNearExpiry 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                        }`}>
                          {product.isNearExpiry ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(product)}
                          className="text-admin-ucla-600 hover:text-admin-ucla-900 dark:text-admin-ucla-400 dark:hover:text-admin-ucla-300 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveNearExpiry(product._id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-overlay flex items-center justify-center overflow-y-auto">
          <div className="fixed inset-0 bg-admin-slate-900/50"></div>
          <div className="relative w-full max-w-2xl mx-auto p-4">
            <div className="bg-white dark:bg-admin-slate-800 rounded-lg shadow-xl relative">
              <div className="px-6 py-4 border-b border-admin-slate-200 dark:border-admin-slate-700">
                <h3 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">
                  {isBulkMode ? 'Bulk Near Expiry Discount' : editingNearExpiry ? 'Edit Near Expiry Discount' : 'Add Near Expiry Discount'}
                </h3>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Product Selection */}
                {!isBulkMode && (
                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Select Product
                    </label>
                    <div className="relative">
                      <input
                        id="product-search-input"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setIsDropdownOpen(true);
                        }}
                        placeholder="Search products..."
                        className="w-full px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 dark:bg-admin-slate-700 dark:text-admin-slate-100"
                      />
                      
                      {isDropdownOpen && (
                        <div id="product-dropdown" className="absolute z-10 w-full mt-1 bg-white dark:bg-admin-slate-700 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md shadow-lg max-h-60 overflow-auto">
                          {isSearching ? (
                            <div className="px-4 py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400">Searching...</div>
                          ) : searchResults.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400">No products found</div>
                          ) : (
                            searchResults.map((product) => (
                              <div
                                key={product._id}
                                onClick={() => handleProductSelect(product)}
                                className="px-4 py-2 hover:bg-admin-slate-100 dark:hover:bg-admin-slate-600 cursor-pointer flex items-center gap-3"
                              >
                                <img
                                  src={product.images?.[0] || '/placeholder-product.jpg'}
                                  alt={product.name}
                                  className="h-8 w-8 rounded object-cover"
                                />
                                <div>
                                  <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                    {formatCurrency(product.regularPrice)} • Expires: {formatExpiryDate(product.expiryDate)}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    
                    {selectedProduct && (
                      <div className="mt-2 p-3 bg-admin-slate-50 dark:bg-admin-slate-700 rounded-md">
                        <div className="flex items-center gap-3">
                          <img
                            src={selectedProduct.images?.[0] || '/placeholder-product.jpg'}
                            alt={selectedProduct.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                          <div>
                            <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                              {selectedProduct.name}
                            </div>
                            <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                              {formatCurrency(selectedProduct.regularPrice)} • Expires: {formatExpiryDate(selectedProduct.expiryDate)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Bulk Product Selection */}
                {isBulkMode && (
                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Select Products ({selectedProducts.length} selected)
                    </label>
                    <div className="relative">
                      <input
                        id="product-search-input"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setIsDropdownOpen(true);
                        }}
                        placeholder="Search products..."
                        className="w-full px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 dark:bg-admin-slate-700 dark:text-admin-slate-100"
                      />
                      
                      {isDropdownOpen && (
                        <div id="product-dropdown" className="absolute z-10 w-full mt-1 bg-white dark:bg-admin-slate-700 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md shadow-lg max-h-60 overflow-auto">
                          {isSearching ? (
                            <div className="px-4 py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400">Searching...</div>
                          ) : searchResults.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-admin-slate-500 dark:text-admin-slate-400">No products found</div>
                          ) : (
                            searchResults.map((product) => {
                              const isSelected = selectedProducts.find(p => p._id === product._id);
                              return (
                                <div
                                  key={product._id}
                                  onClick={() => handleProductSelect(product)}
                                  className={`px-4 py-2 hover:bg-admin-slate-100 dark:hover:bg-admin-slate-600 cursor-pointer flex items-center gap-3 ${
                                    isSelected ? 'bg-admin-ucla-50 dark:bg-admin-ucla-900/30' : ''
                                  }`}
                                >
                                  <img
                                    src={product.images?.[0] || '/placeholder-product.jpg'}
                                    alt={product.name}
                                    className="h-8 w-8 rounded object-cover"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                                      {product.name}
                                    </div>
                                    <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                      {formatCurrency(product.regularPrice)} • Expires: {formatExpiryDate(product.expiryDate)}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <svg className="h-5 w-5 text-admin-ucla-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    
                    {selectedProducts.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-2">
                        {selectedProducts.map((product) => (
                          <div key={product._id} className="p-2 bg-admin-slate-50 dark:bg-admin-slate-700 rounded-md flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img
                                src={product.images?.[0] || '/placeholder-product.jpg'}
                                alt={product.name}
                                className="h-8 w-8 rounded object-cover"
                              />
                              <div>
                                <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                                  {product.name}
                                </div>
                                <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                  {formatCurrency(product.regularPrice)} • Expires: {formatExpiryDate(product.expiryDate)}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleProductSelect(product)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Discount Type
                  </label>
                  <select
                    value={nearExpiryForm.nearExpiryDiscountType}
                    onChange={(e) => setNearExpiryForm({ ...nearExpiryForm, nearExpiryDiscountType: e.target.value })}
                    className="w-full px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 dark:bg-admin-slate-700 dark:text-admin-slate-100"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="amount">Fixed Amount</option>
                  </select>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Discount Value
                    {nearExpiryForm.nearExpiryDiscountType === 'percentage' ? ' (%)' : ' (Rs.)'}
                  </label>
                  <input
                    type="number"
                    value={nearExpiryForm.nearExpiryDiscountValue}
                    onChange={(e) => setNearExpiryForm({ ...nearExpiryForm, nearExpiryDiscountValue: e.target.value })}
                    placeholder={nearExpiryForm.nearExpiryDiscountType === 'percentage' ? 'Enter percentage (e.g., 20)' : 'Enter amount (e.g., 100)'}
                    min="0"
                    max={nearExpiryForm.nearExpiryDiscountType === 'percentage' ? '99' : undefined}
                    className="w-full px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 dark:bg-admin-slate-700 dark:text-admin-slate-100"
                    required
                  />
                </div>

                {/* Expiry Date */}
                <div>
                  <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Expiry Date *
                  </label>
                  <input
                    type="datetime-local"
                    value={nearExpiryForm.expiryDate}
                    onChange={(e) => setNearExpiryForm({ ...nearExpiryForm, expiryDate: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 dark:bg-admin-slate-700 dark:text-admin-slate-100"
                    required
                  />
                  <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 mt-1">
                    Products must have an expiry date to enable near expiry discounts
                  </p>
                </div>

                {/* Enable Near Expiry */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isNearExpiry"
                    checked={nearExpiryForm.isNearExpiry}
                    onChange={(e) => setNearExpiryForm({ ...nearExpiryForm, isNearExpiry: e.target.checked })}
                    className="h-4 w-4 text-admin-ucla-600 focus:ring-admin-ucla-500 border-admin-slate-300 dark:border-admin-slate-600 rounded"
                  />
                  <label htmlFor="isNearExpiry" className="ml-2 block text-sm text-admin-slate-700 dark:text-admin-slate-300">
                    Enable Near Expiry Discount
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-800 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-admin-ucla-500 hover:bg-admin-ucla-600 rounded-md"
                  >
                    {isBulkMode ? 'Apply to Selected Products' : editingNearExpiry ? 'Update Discount' : 'Add Discount'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NearExpiryPage;
